import { useEffect, useRef, useState } from "react";
import { Share2, Facebook, Linkedin, Link2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { copyTextReliable } from "@/lib/clipboard";

function XIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  );
}
function WhatsAppIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm5.8 14.17c-.24.68-1.42 1.31-1.95 1.36-.5.05-1.13.07-1.83-.11-.42-.13-.96-.31-1.66-.61-2.92-1.26-4.83-4.2-4.98-4.39-.14-.19-1.18-1.57-1.18-3s.75-2.13 1.02-2.42c.27-.29.58-.36.78-.36l.56.01c.18.01.42-.07.66.5.24.58.82 2.01.89 2.16.07.14.12.31.02.5-.09.19-.14.31-.28.48-.14.17-.3.37-.42.5-.14.14-.29.29-.12.57.17.29.74 1.22 1.59 1.98 1.09.97 2.01 1.27 2.3 1.42.29.14.46.12.63-.07.17-.19.72-.84.91-1.13.19-.29.39-.24.66-.14.27.09 1.7.8 1.99.95.29.14.48.22.55.34.07.12.07.68-.17 1.36Z" />
    </svg>
  );
}

// Bouton de partage dédié avec menu réseaux (Facebook, WhatsApp, X, LinkedIn, copier)
export default function ShareButton({ url, title = "PROXIreduc-28", text, testId = "share-button", className = "", onShare, variant = "button", wrapperClassName = "inline-block" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const shareText = text || title;
  const e = encodeURIComponent;

  useEffect(() => {
    const onDoc = (ev) => { if (ref.current && !ref.current.contains(ev.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const items = [
    { key: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${e(shareUrl)}`, icon: <Facebook size={16} />, color: "text-[#1877F2]" },
    { key: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${e(shareText + " " + shareUrl)}`, icon: <WhatsAppIcon />, color: "text-[#25D366]" },
    { key: "x", label: "X (Twitter)", href: `https://twitter.com/intent/tweet?url=${e(shareUrl)}&text=${e(shareText)}`, icon: <XIcon />, color: "text-slate-900" },
    { key: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${e(shareUrl)}`, icon: <Linkedin size={16} />, color: "text-[#0A66C2]" },
  ];

  const nativeOrOpen = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (navigator.share) {
      onShare?.("native");
      navigator.share({ title, text: shareText, url: shareUrl }).catch(() => setOpen((v) => !v));
    } else {
      setOpen((v) => !v);
    }
  };

  const onCopy = async (e) => {
    e?.stopPropagation?.();
    const ok = copyTextReliable(shareUrl);
    if (ok) {
      setCopied(true);
      onShare?.("copy");
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Impossible de copier le lien");
    }
  };

  return (
    <div className={`relative ${wrapperClassName}`} ref={ref}>
      {variant === "icon" ? (
        <button type="button" onClick={nativeOrOpen} data-testid={testId} aria-label="Partager"
          className={`w-9 h-9 grid place-items-center rounded-full bg-white/95 hover:bg-slate-50 shadow-sm border border-slate-200 ${className}`}>
          <Share2 size={16} className="text-slate-700" />
        </button>
      ) : (
        <button type="button" onClick={nativeOrOpen} data-testid={testId}
          className={`inline-flex items-center justify-center h-10 px-4 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium transition-colors hover:bg-slate-50 ${className}`}>
          <Share2 size={14} className="mr-1.5" /> Partager
        </button>
      )}
      {open && (
        <div onClick={(e) => e.stopPropagation()} className={`absolute z-50 mt-2 ${variant === "icon" ? "right-0" : "left-0"} w-56 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden`} data-testid={`${testId}-menu`}>
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Partager</span>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700" aria-label="Fermer"><X size={14} /></button>
          </div>
          {items.map((it) => (
            <a
              key={it.key}
              href={it.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                if (it.key === "facebook") {
                  const ok = copyTextReliable(`${shareText}\n${shareUrl}`);
                  if (ok) toast.success("Texte + lien copiés — collez-les (Ctrl/Cmd+V) dans votre groupe Facebook", { duration: 7000 });
                  else toast.message("Copie automatique impossible — copiez le lien manuellement puis collez.", { duration: 7000 });
                }
                onShare?.(it.key);
                setOpen(false);
              }}
              data-testid={`${testId}-${it.key}`}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-sm text-slate-700"
            >
              <span className={it.color}>{it.icon}</span> {it.label}
            </a>
          ))}
          <button
            onClick={onCopy}
            data-testid={`${testId}-copy`}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-sm text-slate-700 border-t border-slate-100"
          >
            <span className="text-slate-500">{copied ? <Check size={16} /> : <Link2 size={16} />}</span>
            {copied ? "Lien copié" : "Copier le lien"}
          </button>
        </div>
      )}
    </div>
  );
}
